import React, { FC, useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { toGygLocale } from '../../../utils/gyg-utils'

const GYG_SCRIPT_SRC = 'https://widget.getyourguide.com/dist/pa.umd.production.min.js'
const GYG_WIDGET_FRAME = 'https://widget.getyourguide.com/default/activities.frame'

// Tracks script load state across SPA navigations. Module-level so it survives
// component unmount/remount — querySelector-based detection breaks when the
// errored script tag persists but has no onerror attached.
let gygScriptState: 'idle' | 'loading' | 'loaded' | 'error' = 'idle'

type Props = {
    destinationName: string
    partnerId: string
    items?: number
    campaign: string
    locale?: string
    onError?: () => void
}

const GYGActivityWidget: FC<Props> = ({
    destinationName,
    partnerId,
    items = 3,
    campaign,
    locale = 'de-DE',
    onError,
}) => {
    const [containerRef, inView] = useInView({ rootMargin: '200px', triggerOnce: true })
    // Incremented when script is already loaded on SPA remount to force the widget div
    // to remount, giving GYG's MutationObserver a new element to detect.
    const [widgetKey, setWidgetKey] = useState(0)
    // Ref so the script onerror handler always calls the latest onError without
    // it appearing in the effect dependency array (which would re-fire the effect
    // on every parent render and cause unintended widgetKey increments).
    const onErrorRef = useRef(onError)
    useEffect(() => {
        onErrorRef.current = onError
    }, [onError])

    useEffect(() => {
        if (!inView) {
            return
        }

        if (gygScriptState === 'loaded') {
            // Script already loaded (SPA navigation): force widget div remount so
            // GYG's MutationObserver detects the new element.
            setWidgetKey(k => k + 1)
            return
        }

        if (gygScriptState === 'error') {
            onErrorRef.current?.()
            return
        }

        if (gygScriptState === 'loading') {
            // Script in flight; GYG will scan existing widget divs on load.
            return
        }

        // First call — inject script singleton.
        gygScriptState = 'loading'
        const script = document.createElement('script')
        script.src = GYG_SCRIPT_SRC
        script.async = true
        script.addEventListener(
            'load',
            () => {
                gygScriptState = 'loaded'
            },
            { once: true },
        )
        script.addEventListener(
            'error',
            () => {
                gygScriptState = 'error'
                onErrorRef.current?.()
            },
            { once: true },
        )
        document.body.append(script)
    }, [inView])

    return (
        <div ref={containerRef}>
            {inView && (
                <div
                    key={widgetKey}
                    data-gyg-href={GYG_WIDGET_FRAME}
                    data-gyg-widget="activities"
                    data-gyg-partner-id={partnerId}
                    data-gyg-q={destinationName}
                    data-gyg-number-of-items={items}
                    data-gyg-locale-code={toGygLocale(locale)}
                    data-gyg-cmp={campaign}
                />
            )}
        </div>
    )
}

export default GYGActivityWidget
